/**
 * <rm-tabs> — accessible tabs (WAI-ARIA tabs pattern, automatic activation).
 *
 * Markup contract:
 *   <rm-tabs>
 *     <div data-tablist role="tablist"> <button role="tab" aria-controls="panel-id">…</button> … </div>
 *     <button data-tabs-next>  (optional: scrolls an overflowing tab bar)
 *     <div role="tabpanel" id="panel-id" hidden>…</div>
 *   </rm-tabs>
 */
if (!customElements.get('rm-tabs')) {
  class RmTabs extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.nextButton = this.querySelector('[data-tabs-next]');
    this.prevButton = this.querySelector('[data-tabs-prev]');
      if (!this.tablist || this.tabs.length === 0) return;

      this.onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) this.select(tab, false);
      };
      this.onKeydown = (event) => {
        const index = this.tabs.indexOf(document.activeElement);
        if (index === -1) return;
        let target = null;
        if (event.key === 'ArrowRight') target = this.tabs[(index + 1) % this.tabs.length];
        if (event.key === 'ArrowLeft') target = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        if (event.key === 'Home') target = this.tabs[0];
        if (event.key === 'End') target = this.tabs[this.tabs.length - 1];
        if (!target) return;
        event.preventDefault();
        this.select(target, true);
      };
      this.onPrevClick = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.tablist.scrollBy({ left: -this.tablist.clientWidth * 0.6, behavior: reduced ? 'auto' : 'smooth' });
      };
    this.onNextClick = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.tablist.scrollBy({ left: this.tablist.clientWidth * 0.6, behavior: reduced ? 'auto' : 'smooth' });
      };
      this.onScroll = () => this.updateOverflow();

      this.tablist.addEventListener('click', this.onClick);
      this.tablist.addEventListener('keydown', this.onKeydown);
      this.tablist.addEventListener('scroll', this.onScroll, { passive: true });
      this.nextButton?.addEventListener('click', this.onNextClick);
    this.prevButton?.addEventListener('click', this.onPrevClick);
      this.resizeObserver = new ResizeObserver(() => this.updateOverflow());
      this.resizeObserver.observe(this.tablist);
      this.updateOverflow();
    }

    disconnectedCallback() {
      this.tablist?.removeEventListener('click', this.onClick);
      this.tablist?.removeEventListener('keydown', this.onKeydown);
      this.tablist?.removeEventListener('scroll', this.onScroll);
      this.nextButton?.removeEventListener('click', this.onNextClick);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.resizeObserver?.disconnect();
    }

    select(selectedTab, moveFocus) {
      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !isSelected;
      });
      if (moveFocus) selectedTab.focus();
      selectedTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    updateOverflow() {
      const max = this.tablist.scrollWidth - this.tablist.clientWidth;
      this.toggleAttribute('data-overflowing', max > 1);
      if (this.nextButton) this.nextButton.hidden = this.tablist.scrollLeft >= max - 1 || max <= 1;
      if (this.prevButton) this.prevButton.hidden = this.tablist.scrollLeft <= 1 || max <= 1;
    }
  }

  customElements.define('rm-tabs', RmTabs);
}
